import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toastSomethingError } from '@/components/ui/toast-content';
import PATH_PAGE from '@/router/path';
import { getWorkspaces } from '@/service/workspace';
import { useCreateWorkspace } from '@/service/workspace/hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const CreateWorkspace: React.FC = () => {

  const { mutateAsync, isPending } = useCreateWorkspace({
    onError: () => {
      toastSomethingError()
    },
  });
  const canGoBack = window?.history?.state?.idx !== 0;
  const navigate = useNavigate();
  const formSchema = z.object({
    workspaceName: z.string().min(2).max(50),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspaceName: '',
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const workspaces = await getWorkspaces();
    if (
      workspaces.filter(
        (workspace) => workspace.workspace_name === data.workspaceName
      ).length > 0
    ) {
      form.setError('workspaceName', {
        type: 'manual',
        message: 'Workspace name already exists',
      });
      return;
    }
    const result = await mutateAsync({
      workspace_name: data.workspaceName,
    });
    navigate(PATH_PAGE.dashboard.workspaceById(result?.workspace_id));
  };

  const handleCancel = () => {
    if (canGoBack) navigate(-1);
    else navigate(PATH_PAGE.setting.workspaces.root);
  };

  return (
    <div className="bg-foreground/10">
      <div className="container flex h-screen items-center justify-center">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full max-w-[500px]"
          >
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="text-lg">Create a new Workspace</span>
                </CardTitle>
                <CardDescription>
                  Elevate your project management with a workspace
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="workspaceName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Workspace Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="workspace-1" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  variant="outline"
                  disabled={isPending}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button disabled={isPending} type="submit">
                  Create
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default CreateWorkspace;
